<script setup lang="ts">
import { defineProps, ref, watch } from 'vue'
import { ArrowDownTrayIcon } from '@heroicons/vue/24/outline'
import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale'
import {
  type IDoesFilterPassParams,
  type SelectionChangedEvent,
  type FilterChangedEvent,
  type PaginationChangedEvent,
  themeMaterial
} from 'ag-grid-community'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import { useToast } from 'vue-toast-notification'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

const emit = defineEmits(['onSelectionChanged', 'selectionChanged', 'gridReady', 'onFilterChanged', 'onPaginationChanged', 'onSortChanged'])
const $toast = useToast()
const props = defineProps({
  list: {
    type: Array as () => any[],
    required: true
  },
  cols: {
    type: Array as () => any[],
    required: true
  },
  /**
   * @property {Object} type - l'objet peut être en selection simple ou multiple : {mode: 'multiRow'}
   */
  rowSelection: {
    type: Object as () => {
      mode: string | null
      selectAll: string
    },
    default: () => ({
      mode: undefined,
      selectAll: 'filtered'
    })
  },
  paginationPageSize: {
    type: Number,
    default: 10
  },
  paginationTotalRows: {
    type: Number,
    default: 0
  },
  currentPage: {
    type: Number,
    default: 1
  },
  totalPages: {
    type: Number,
    default: 1
  },
  useServerSidePagination: {
    type: Boolean,
    default: false
  },
  externalFilterColumn: {
    type: String,
    required: false,
    default: ''
  },
  externalFilterValue: {
    type: String,
    required: false,
    default: ''
  },
  getRowId: {
    type: Function,
    required: false,
    default: undefined
  },
  getRowStyle: {
    type: Function,
    required: false,
    default: undefined
  },
  filterModel: {
    type: Object as () => Record<string, any>,
    required: false,
    default: undefined
  },
  getRowHeight: {
    type: Function,
    required: false,
    default: undefined
  },
  showExport: {
    type: Boolean,
    default: true
  }
})

const myTheme = themeMaterial.withParams({
  rangeSelectionBorderStyle: 'none',
  accentColor: '#175a7c'
})

const gridApi = ref<any>(null)

function getParamsExport() {
  const tab = props.cols.filter((col: any) => !['actions', 'contacts', 'tag'].includes(col.field))
  const columnsToFormat = ['_expectedRevenue', '_recurringRevenue', '_totalRevenue']

  return {
    columnKeys: tab.map((col: any) => col.field),
    processCellCallback: (params: any) => {
      if (columnsToFormat.includes(params.column.getColId())) {
        return params.value.replace(/\s+/g, '').replace('€', '').trim()
      }
      return params.value
    }
  }
}

const externalFilterParams = ref({
  column: '',
  value: ''
})

function isExternalFilterPresent() {
  return !!externalFilterParams.value.value
}

function doesExternalFilterPass(node: IDoesFilterPassParams) {
  const { column, value } = externalFilterParams.value
  if (!column || !value) return true

  return String(node.data[column]).toLowerCase().includes(value.toLowerCase())
}

function onBtnExport() {
  try {
    const data = getVisibleRowDataForExport()
    if (!data.length) {
      $toast.warning('Aucune donnée à exporter')
      return
    }
    const worksheet = XLSX.utils.json_to_sheet(data)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Export')

    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })

    const filename = `export_${new Date().toISOString().split('T')[0]}.xlsx`
    saveAs(blob, filename)
  } catch (e) {
    console.error(e)
    $toast.error('Grid API non disponible pour l’exportation')
  }
}

function onSelectionChanged(params: SelectionChangedEvent) {
  const selectedRows = params.api.getSelectedRows()
  emit('onSelectionChanged', selectedRows)
}

function getVisibleRowDataForExport() {
  const api = gridApi.value?.api ?? gridApi.value
  if (!api) return []

  const params = getParamsExport()
  const includedCols = new Set(params.columnKeys)

  const rows: any[] = []

  const getNestedValue = (obj: any, path: string) => {
    if (!path) return undefined
    return path.split('.').reduce((acc, part) => acc && acc[part], obj)
  }

  api.forEachNodeAfterFilterAndSort((node: any) => {
    const row: Record<string, any> = {}
    for (const col of props.cols) {
      if (!includedCols.has(col.field)) continue

      let raw = getNestedValue(node.data, col.field)

      if (col.valueFormatter) {
        raw = col.valueFormatter({
          value: raw,
          data: node.data,
          column: { getColId: () => col.field },
          node: node
        })
      }

      row[col.headerName ?? col.field] = params.processCellCallback
        ? params.processCellCallback({ value: raw, column: { getColId: () => col.field } })
        : raw
    }
    rows.push(row)
  })
  return rows
}

watch(
  () => props.filterModel,
  (newModel) => {
    if (gridApi.value?.api) {
      gridApi.value.api.setFilterModel(newModel ?? null)
    }
  },
  { deep: true }
)

// Synchroniser les props avec le filtre interne si elles changent
watch(
  () => [props.externalFilterColumn, props.externalFilterValue],
  ([newColumn, newFilter]) => {
    externalFilterParams.value.column = newColumn
    externalFilterParams.value.value = newFilter

    if (gridApi.value?.api) {
      gridApi.value.api.onFilterChanged()
    } else {
      gridApi.value?.onFilterChanged()
    }
  },
  { immediate: true }
)

function onGridReady(params: any) {
  gridApi.value = params.api
  if (props.filterModel) {
    params.api.setFilterModel(props.filterModel)
  }
  emit('gridReady', params)
}

function onFilterChanged(event: FilterChangedEvent) {
  const filters = event.api.getFilterModel()
  emit('onFilterChanged', filters)
}

function onSortChanged(event: any) {
  const [col] = event.columns
  if (col && col.colId && col.sort) {
    emit('onSortChanged', { colId: col.colId, sort: col.sort })
  } else {
    emit('onSortChanged', null)
  }
}

function onPaginationChanged(event: PaginationChangedEvent) {
  if (props.useServerSidePagination) return // Ignore AG Grid pagination events when using custom pagination

  const pageSize = event.api.paginationGetPageSize()
  const currentPage = event.api.paginationGetCurrentPage() + 1

  emit('onPaginationChanged', pageSize, currentPage)
}

function goToPage(page: number) {
  if (page < 1 || page > props.totalPages) return
  emit('onPaginationChanged', props.paginationPageSize, page)
}

function changePageSize(newSize: number) {
  emit('onPaginationChanged', newSize, 1) // Reset to page 1 when changing page size
}
</script>

<template>
  <div class="flex flex-wrap justify-between items-center my-1 gap-3">
    <div class="flex-shrink-0">
      <slot name="title" />
    </div>
    <div class="flex flex-1 flex-nowrap justify-end gap-4 items-end">
      <slot name="filter" />
      <slot name="button" />
      <ButtonDaisy v-if="showExport" @click="onBtnExport()" text="Export" variant="secondary" :icon="ArrowDownTrayIcon"
                   class="mb-1" />
    </div>
  </div>
  <ag-grid-vue
    ref="gridApi"
    id="grid"
    domLayout="autoHeight"
    @selection-changed="onSelectionChanged"
    @grid-ready="onGridReady"
    :style="{ height: 'auto' }"
    :getRowStyle="getRowStyle"
    :getRowHeight="props.getRowHeight"
    :animateRows="false"
    :localeText="AG_GRID_LOCALE_FR"
    :rowData="list"
    :columnDefs="cols"
    :defaultColDef="{
      resizable: true,
      filter: true,
      flex: 1,
      cellClass: 'border-0',
    }"
    :rowSelection="rowSelection"
    :getRowId="getRowId"
    :pagination="!useServerSidePagination"
    :suppressExcelExport="true"
    :suppressMultiSort="true"
    :paginationPageSizeSelector="useServerSidePagination ? false : [5, 10, 20, 50, 100, 200]"
    :paginationPageSize="paginationPageSize"
    :suppressPaginationPanel="useServerSidePagination"
    :enableCellTextSelection="true"
    :ensureDomOrder="true"
    :accentedSort="true"
    :theme="myTheme"
    :isExternalFilterPresent="isExternalFilterPresent"
    :doesExternalFilterPass="doesExternalFilterPass"
    @filter-changed="onFilterChanged"
    @sort-changed="onSortChanged"
    @pagination-changed="onPaginationChanged"
  >
  </ag-grid-vue>

  <!-- Custom Server-Side Pagination -->
  <div v-if="useServerSidePagination" class="flex justify-end items-center gap-4">
    <div class="flex items-center gap-2">
      <span class="text-sm text-nowrap">Lignes par page :</span>
      <select class="select select-sm" :value="paginationPageSize"
              @change="changePageSize(Number(($event.target as HTMLSelectElement).value))">
        <option :value="5">5</option>
        <option :value="10">10</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
        <option :value="200">200</option>
        <option :value="500">500</option>
      </select>
    </div>

    <div class="join">
      <button class="join-item btn btn-sm" :disabled="currentPage === 1" @click="goToPage(1)">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
             class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5" />
        </svg>
      </button>
      <button class="join-item btn btn-sm" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
             class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
      </button>
      <button class="join-item btn btn-sm">Page {{ currentPage }} / {{ totalPages }}</button>
      <button class="join-item btn btn-sm" :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
             class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
      </button>
      <button class="join-item btn btn-sm" :disabled="currentPage === totalPages" @click="goToPage(totalPages)">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
             class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5.25 4.5l7.5 7.5-7.5 7.5m6-15l7.5 7.5-7.5 7.5" />
        </svg>
      </button>
    </div>
  </div>
</template>

<style>
#grid {
  border-radius: 8px !important;
  overflow: hidden;

  .ag-cell {
    padding-block: 4px;
    padding-inline: 8px;
  }

  .ag-header-cell {
    padding-block: 6px;
    padding-inline: 8px;
  }

  .grid-action,
  .grid-action .ag-cell-value {
    overflow: visible;
  }

  .grid-action .ag-cell-value,
  .grid-action .ag-cell-value div {
    margin: 0 !important;
  }

  .grid-action .ag-cell-wrapper {
    height: inherit;
  }
}
</style>
